export type ApiErrorResponse = {
  message: string
  issues: unknown
}

import {
  DepartmentEntryMode,
  InvitationStatus,
  TenantContextSelectionSource,
  WorkspaceStructureType,
} from "@ibada/shared-types/auth"

export {
  DepartmentEntryMode,
  InvitationStateErrorCode,
  InvitationStatus,
  TenantContextSelectionSource,
  WorkspaceStructureType,
} from "@ibada/shared-types/auth"

import {
  UserBranchAccessStatus,
  WorkspaceAccessModule,
  WorkspaceAccessScope,
  WorkspaceMembershipRole as WorkspaceAccessRole,
  WorkspaceRoleKey,
  WorkspaceUserStatus as UserStatus,
} from "@ibada/shared-types/workspace"

export {
  RoleKey,
  RoleScope,
  RoleStatus,
  WorkspaceMembershipRole,
  /** @deprecated Prefer `WorkspaceMembershipRole` */
  WorkspaceMembershipRole as WorkspaceAccessRole,
  WorkspaceRoleKey,
  WorkspaceUserStatus,
  /** @deprecated Prefer `WorkspaceUserStatus` */
  WorkspaceUserStatus as UserStatus,
  WorkspaceAccessModule,
  UserBranchAccessStatus,
  WorkspaceAccessPermission,
  BudgetAccessLevel,
  WorkspaceAccessScope,
} from "@ibada/shared-types/workspace"

export const WORKSPACE_MODULES: WorkspaceAccessModule[] = [
  WorkspaceAccessModule.MEMBERS,
  WorkspaceAccessModule.FINANCE,
  WorkspaceAccessModule.BUDGETING,
  WorkspaceAccessModule.ACTIVITIES,
  WorkspaceAccessModule.REPORTS,
  WorkspaceAccessModule.SETTINGS,
  WorkspaceAccessModule.ASSETS,
  WorkspaceAccessModule.GROUPS,
  WorkspaceAccessModule.MINISTRIES,
  WorkspaceAccessModule.DEPARTMENTS,
  WorkspaceAccessModule.FORMS,
]

export const BRANCH_ACCESS_MODULES: WorkspaceAccessModule[] = [
  WorkspaceAccessModule.MEMBERS,
  WorkspaceAccessModule.FINANCE,
  WorkspaceAccessModule.BUDGETING,
  WorkspaceAccessModule.ASSETS,
  WorkspaceAccessModule.FORMS,
  WorkspaceAccessModule.ACTIVITIES,
  WorkspaceAccessModule.REPORTS,
  WorkspaceAccessModule.GROUPS,
  WorkspaceAccessModule.MINISTRIES,
  WorkspaceAccessModule.DEPARTMENTS,
]

export const MEMBER_EDIT_INHERITED_BRANCH_ACCESS_MODULES: WorkspaceAccessModule[] = [
  WorkspaceAccessModule.GROUPS,
  WorkspaceAccessModule.MINISTRIES,
]

export const ASSIGNABLE_BRANCH_ACCESS_MODULES: WorkspaceAccessModule[] = BRANCH_ACCESS_MODULES.filter(
  (module) => !MEMBER_EDIT_INHERITED_BRANCH_ACCESS_MODULES.includes(module),
)

/**
 * The real, branch-scoped modules that constitute parish/organization access. Parish scope = the workspace
 * SYSTEM branch, and access is evaluated on these real modules at that branch — there is no separate PARISH_*
 * module anymore. Drives which modules the SYSTEM-branch team/invite/permission editors offer. MEMBERS is
 * excluded because it has no parish-wide surface.
 */
export const PARISH_ACCESS_MODULES: WorkspaceAccessModule[] = [
  WorkspaceAccessModule.FINANCE,
  WorkspaceAccessModule.BUDGETING,
  WorkspaceAccessModule.GROUPS,
  WorkspaceAccessModule.MINISTRIES,
  WorkspaceAccessModule.ACTIVITIES,
]

export const SYSTEM_ACCESS_MODULES: WorkspaceAccessModule[] = [WorkspaceAccessModule.SETTINGS]

export function getDefaultAccessScopeForModule(module: WorkspaceAccessModule): WorkspaceAccessScope {
  // SETTINGS is the only workspace-scoped module. Parish access to the operational modules
  // (FINANCE/GROUPS/MINISTRIES/ACTIVITIES/BUDGETING) is expressed by granting the real, branch-scoped
  // module on the workspace SYSTEM branch — there is no separate parish module.
  if (module === WorkspaceAccessModule.SETTINGS) {
    return WorkspaceAccessScope.WORKSPACE
  }

  return WorkspaceAccessScope.BRANCH
}

/** Matches Prisma `WorkspaceStructureType` and `/tenant/auth/me` workspace payloads. */
export const WORKSPACE_STRUCTURE_TYPES = [
  WorkspaceStructureType.SingleBranch,
  WorkspaceStructureType.MultiBranch,
] as const

export type WorkspaceSummary = {
  id: string
  name: string
  displayName?: string | null
  slug: string
  logoUrl: string | null
  status: string
  structureType: WorkspaceStructureType
  /**
   * The parish/SYSTEM branch, sent only when this user holds ORGANIZATION scope on a multi-branch workspace.
   *
   * Organization roles authorize the parish without creating a `UserBranchAccess` row for it, so this is how
   * the parish reaches the branch switcher for a user who governs the whole workspace. Absent/null means the
   * parish is reachable only through an explicit branch grant, if at all.
   */
  systemBranch?: { id: string; name: string; slug: string } | null
  /** The sole NORMAL branch of a mono workspace; identity only, never an access grant. */
  operationalBranch?: { id: string; name: string; slug: string } | null
  /** When the workspace began. The free-trial window is derived from it — see `resolveTrialWindow`. */
  createdAt?: string | null
}

export type InvitationRecord = {
  id: string
  email: string
  workspaceId: string | null
  isSystemInvite: boolean
  roleKey: string | null
  invitedByUserId: string | null
  code: string
  status: InvitationStatus
  expiresAt: string
  viewedAt: string | null
  acceptedAt: string | null
  declinedAt: string | null
  createdAt: string
  updatedAt: string
  workspace: WorkspaceSummary | null
}

export type AuthMeInvitationSummary = {
  id: string
  code: string
  email: string
  workspaceId: string | null
  isSystemInvite: boolean
  roleKey: string | null
  status: InvitationStatus
  expiresAt: string
  viewedAt: string | null
  createdAt: string
  workspace: WorkspaceSummary | null
}

/** Values returned by `GET /tenant/auth/me` for `state` when the user has no workspace memberships. */
export enum AuthMeWorkspaceState {
  NO_WORKSPACE_ACCESS = "NO_WORKSPACE_ACCESS",
}

export type AuthMeResponse = {
  auth: {
    clerkUserId: string
    localUserId: string
    email: string
    sessionId: string | null
    isAuthenticated: true
  }
  /**
   * Server-computed fingerprint of the effective authorization graph for this user.
   * Changes whenever roles, branch access, module permissions, or role permission definitions change.
   */
  authorizationRevision?: string
  user: {
    id: string
    email: string
    defaultWorkspaceId: string | null
    clerkUserId: string | null
    emailVerifiedAt: string | null
    lastLoginAt: string | null
    firstName: string
    lastName: string
    phone: string | null
    avatarUrl: string | null
    status: UserStatus
    createdAt: string
    updatedAt: string
  }
  pendingInvitations: AuthMeInvitationSummary[]
  expiredInvitations: AuthMeInvitationSummary[]
  /** Set when the user has no workspace memberships; `null` when they have workspace access. */
  state: AuthMeWorkspaceState | null
  workspaceAccess: Array<{
    id: string
    workspace: {
      id: string
      name: string
      displayName?: string | null
      slug: string
      description: string | null
      logoUrl: string | null
      email: string | null
      phone: string | null
      defaultLanguage: string
      status: "PENDING" | "ACTIVE" | "INACTIVE" | "SUSPENDED"
      structureType: WorkspaceStructureType
      timezone: string
      /**
       * The parish/SYSTEM branch, sent only when this user holds ORGANIZATION scope on a multi-branch
       * workspace. Organization roles authorize the parish without creating a `UserBranchAccess` row, so this
       * is how the parish reaches the branch switcher for someone who governs the whole workspace.
       */
      systemBranch?: { id: string; name: string; slug: string; timezone?: string } | null
      operationalBranch?: { id: string; name: string; slug: string; timezone?: string } | null
      createdAt: string
      updatedAt: string
    }
    member:
      | {
          id: string
          workspaceId: string
          branchId: string
          firstName: string
          lastName: string
          email: string | null
          phone: string | null
          avatarUrl: string | null
          branch: {
            id: string
            workspaceId: string
            name: string
            slug: string
            status: string
            branchType?: "NORMAL" | "SYSTEM"
          } | null
        }
      | null
    workspaceRole?: WorkspaceAccessRole
    userBranchAccesses?: Array<{
      id: string
      workspaceUserId?: string | null
      branchId: string
      status: UserBranchAccessStatus
      /** Branch-level authority (ADMIN governs this branch; USER is a normal branch member). */
      branch: {
        id: string
        workspaceId: string
        name: string
        slug: string
        status: string
        branchType?: "NORMAL" | "SYSTEM"
        /** The branch's accounting currency (IBA-114) — what every branch-scoped finance figure is denominated in. */
        baseCurrency?: string | null
        timezone?: string | null
      } | null
      createdAt?: string | null
      updatedAt?: string | null
    }>
    status: "PENDING" | "ACTIVE" | "SUSPENDED"
    roles: Array<{
      id: string
      roleKey: string
      roleName: string
      roleScope: "WORKSPACE" | "BRANCH"
      /** Only ACTIVE role assignments grant client-side permissions. */
      roleStatus?: "ACTIVE" | "INACTIVE"
      branchId: string | null
      branch: {
        id?: string | null
        slug?: string | null
        name?: string | null
        branchType?: "NORMAL" | "SYSTEM" | null
      } | null
      /** Permission strings (`<module>.<action>`) granted by this assigned custom role. */
      permissions?: string[]
    }>
    departmentAccess?: Array<{
      departmentId: string
      departmentName: string
      departmentStatus: string
      branchId: string
      branchName: string
      branchSlug: string
      roleKeys: string[]
      permissions: string[]
    }>
    departmentEntry?: {
      mode: DepartmentEntryMode
      landingDepartmentId: string | null
    }
    createdAt: string
    updatedAt: string
  }>
}

export type TenantBootstrapResponse = {
  auth: AuthMeResponse["auth"]
  user: AuthMeResponse["user"]
  authorizationRevision?: AuthMeResponse["authorizationRevision"]
  state: AuthMeResponse["state"]
  workspaceAccess: AuthMeResponse["workspaceAccess"]
  startup: {
    redirect: {
      kind: "admin" | "no-access" | "workspaces" | "create-church" | "account-blocked" | "invitation-detail" | "invitation-list"
      href: string
    } | null
    activeWorkspaceCount: number
    workspaceAccessHistoryCount: number
    pendingInvitationCount: number
  }
  currentContext: TenantAccessResolveResponse
}

export type ResolveWorkspaceInvitationResponse = {
  id: string
  code: string
  workspaceId: string
  roleKey: WorkspaceRoleKey
  status: "pending"
  email: string
  expiresAt: string
  viewedAt: string | null
  createdAt: string
  workspace: WorkspaceSummary
  setup: {
    hasBranches: boolean
    requiresSetup: boolean
    branchCount: number
  }
  acceptance?: {
    canAccept: boolean
    reason?: string | null
    workspace?: WorkspaceSummary | null
  }
}

export type InvitationPreviewStatus = "pending" | "accepted" | "expired" | "revoked" | "invalid"

export type InvitationPreviewResponse = {
  invitationId: string | null
  email: string | null
  isSystemInvite?: boolean
  workspace: {
    id: string
    name: string
    displayName: string
    slug: string
  } | null
  inviterName: string | null
  status: InvitationPreviewStatus
  /** Backend existence-check preflight: route the invitee to sign-in vs sign-up before Clerk. */
  authMode?: "sign_in" | "sign_up" | null
  expiresAt: string | null
}

export type AcceptWorkspaceInvitationRequest = {
  code: string
  workspaceId: string
}

export type AcceptWorkspaceInvitationResponse = {
  accepted?: boolean
  alreadyAccepted?: boolean
  workspaceId?: string
  workspaceSlug?: string | null
  branchSlug?: string | null
  shouldSwitchWorkspaceAvailable?: boolean
  redirectTo?: string | null
  invitation: InvitationRecord
  authContext: AuthMeResponse | null
  onboarding: {
    workspaceId: string
    roleKey: WorkspaceRoleKey
    isOwner: boolean
    hasBranches: boolean
    requiresSetup: boolean
    branchCount: number
  }
  redirect: {
    workspaceSlug: string
    branchSlug: string
    href: string
  } | null
}

export type DeclineWorkspaceInvitationRequest = {
  code: string
  workspaceId: string
}

export type SetDefaultWorkspaceRequest = {
  workspaceId: string
}

export type DeclineWorkspaceInvitationResponse = InvitationRecord

export type MarkWorkspaceInvitationsViewedRequest = {
  invitationIds: string[]
}

export type MarkWorkspaceInvitationsViewedResponse = {
  markedViewedCount: number
}

/** Matches backend `TenantAccessDeniedReason` on `/tenant/auth/tenant-access/resolve`. */
export enum TenantAccessDeniedReason {
  NO_WORKSPACE_ACCESS = "NO_WORKSPACE_ACCESS",
  WORKSPACE_NOT_ACCESSIBLE = "WORKSPACE_NOT_ACCESSIBLE",
  BRANCH_NOT_FOUND_IN_WORKSPACE = "BRANCH_NOT_FOUND_IN_WORKSPACE",
  BRANCH_NOT_ACCESSIBLE = "BRANCH_NOT_ACCESSIBLE",
  REQUIRES_BRANCH_SELECTION = "REQUIRES_BRANCH_SELECTION",
}

/** Branch row in `branchContext.accessibleBranches` from tenant-access resolve. */
export type TenantAccessibleBranchSummary = {
  id: string
  workspaceId: string
  name: string
  slug: string
  status: string
  branchType: string | null
  /** The branch's accounting currency (IBA-114) — what every finance figure in this branch is denominated in. */
  baseCurrency: string | null
}

/** Matches backend `branchContext` on `/tenant/auth/tenant-access/resolve`. */
export type TenantAccessBranchContext = {
  accessibleBranches: TenantAccessibleBranchSummary[]
  hasNormalBranchAccess: boolean
  hasSystemBranchAccess: boolean
  requiresBranchSelection: boolean
  requiresWorkspaceSetup: boolean
}

/** Role scope on tenant-access resolve membership rows (Prisma `RoleScope`). */
export enum TenantAccessRoleScope {
  WORKSPACE = "WORKSPACE",
  BRANCH = "BRANCH",
}

export type TenantAccessResolveResponse = {
  selectionSource: TenantContextSelectionSource
  canonicalLanding: string | null
  invalidRequestReason: TenantAccessDeniedReason | null
  recoveryLanding: string | null
  request: {
    workspaceSlug: string | null
    branchSlug: string | null
  }
  resolved: {
    workspace: WorkspaceSummary | null
    branch: {
      id: string
      workspaceId: string
      name: string
      slug: string
      status: string
      branchType: "NORMAL" | "SYSTEM" | null
    } | null
  }
  defaults: {
    workspaceApplied: boolean
    branchApplied: boolean
  }
  access: {
    allowed: boolean
    hasWorkspaceAccess: boolean
    hasBranchAccess: boolean
    reason: TenantAccessDeniedReason | null
  }
  suggestions: {
    defaultWorkspaceSlug: string | null
    defaultBranchSlug: string | null
  }
  membership: {
    workspaceUserId: string | null
    status: string | null
    roles: Array<{
      roleKey: string
      roleName: string
      roleScope: TenantAccessRoleScope
      branchId: string | null
      branchSlug: string | null
    }>
  }
  branchContext: TenantAccessBranchContext
}

export type CreateBranchRequest = {
  workspaceId: string
  name: string
  status?: "PENDING" | "ACTIVE" | "INACTIVE" | "SUSPENDED"
}

export type CreateBranchResponse = {
  id: string
  workspaceId: string
  name: string
  slug: string
  status: "PENDING" | "ACTIVE" | "INACTIVE" | "SUSPENDED"
  workspace: WorkspaceSummary | null
  createdAt: string
  updatedAt: string
}
